export const DRAFT_KEY = "franquicias-demo-v1";
export const TIME_ZONE = "America/Santo_Domingo";
export const intentions = [
  "Adquirir una franquicia y dirigir mi propia oficina",
  "Invertir en un negocio con una marca ya establecida",
  "Trabajar como agente inmobiliario",
  "Solo estoy explorando información general",
];
export const capitals = [
  "Entre US$8,000 y US$15,000",
  "Entre US$15,000 y US$30,000",
  "Entre US$30,000 y US$50,000",
  "Más de US$50,000",
];
export const zones = [
  "Santiago",
  "La Vega / Bonao",
  "San Francisco de Macorís / Nordeste",
  "Puerto Plata / Costa Norte",
  "San Cristóbal / Sur",
  "Punta Cana",
  "Este (Higüey, La Romana)",
  "Santo Domingo / Distrito Nacional",
  "Aún no lo he definido",
];
export const hours = [
  "9:00 a.m.",
  "10:00 a.m.",
  "11:00 a.m.",
  "2:00 p.m.",
  "3:00 p.m.",
  "4:00 p.m.",
  "5:00 p.m.",
];
export type Step =
  "intro" | "p1" | "p2" | "p3" | "p4" | "p5" | "ok" | "pa" | "pb";
export interface Answers {
  intencion: string;
  capital: string;
  zona: string;
  nombre: string;
  telefono: string;
  email: string;
  consent: boolean;
}
export interface Appointment {
  fecha: string;
  hora: string;
}
export interface Draft {
  version: 1;
  step: Step;
  answers: Answers;
  appointment: Appointment;
}
export const emptyAnswers: Answers = {
  intencion: "",
  capital: "",
  zona: "",
  nombre: "",
  telefono: "",
  email: "",
  consent: false,
};
export const emptyAppointment: Appointment = { fecha: "", hora: "" };
export const normalizeName = (value: string) =>
  value.trim().replace(/\s+/g, " ");
export function contactErrors(a: Answers) {
  return {
    nombre:
      normalizeName(a.nombre).length < 3 ||
      !normalizeName(a.nombre).includes(" "),
    telefono: a.telefono.replace(/\D/g, "").length < 10,
    email: !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(a.email.trim()),
    consent: !a.consent,
  };
}
export function localDate(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  return ["year", "month", "day"]
    .map((k) => parts.find((p) => p.type === k)!.value)
    .join("-");
}
export function businessDays(now = new Date()): string[] {
  const day = new Date(localDate(now) + "T12:00:00Z"),
    result: string[] = [];
  while (result.length < 14) {
    day.setUTCDate(day.getUTCDate() + 1);
    if (![0, 6].includes(day.getUTCDay()))
      result.push(day.toISOString().slice(0, 10));
  }
  return result;
}
export function isBusy(date: string, index: number) {
  const d = new Date(date + "T12:00:00Z");
  return (d.getUTCDate() * 31 + d.getUTCMonth() * 7 + index * 13) % 10 < 3;
}
export const dateLabel = (
  date: string,
  options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "long",
  },
) =>
  new Intl.DateTimeFormat("es-DO", { ...options, timeZone: TIME_ZONE }).format(
    new Date(date + "T12:00:00Z"),
  );
export function validAppointment(a: Appointment, dates = businessDays()) {
  return (
    dates.includes(a.fecha) &&
    hours.includes(a.hora) &&
    !isBusy(a.fecha, hours.indexOf(a.hora))
  );
}
export function previous(step: Step): Step {
  return (
    {
      intro: "intro",
      p1: "intro",
      p2: "p1",
      p3: "p2",
      p4: "p3",
      p5: "p4",
      pa: "p1",
      pb: "p1",
      ok: "intro",
    } as Record<Step, Step>
  )[step];
}
export function restoreDraft(raw: string | null): Draft | null {
  try {
    const value = JSON.parse(raw || "null");
    if (
      !value ||
      value.version !== 1 ||
      !value.answers ||
      !["intro", "p1", "p2", "p3", "p4", "p5", "pa", "pb"].includes(value.step)
    )
      return null;
    const answers = { ...emptyAnswers };
    (Object.keys(answers) as (keyof Answers)[]).forEach((key) => {
      if (key === "consent") answers.consent = value.answers.consent === true;
      else if (typeof value.answers[key] === "string")
        answers[key] = value.answers[key].slice(0, 500);
    });
    if (!intentions.includes(answers.intencion)) answers.intencion = "";
    if (!capitals.includes(answers.capital)) answers.capital = "";
    if (!zones.includes(answers.zona)) answers.zona = "";
    let step: Step = value.step;
    if (step === "pa" || step === "pb") {
      if (answers.intencion !== intentions[step === "pa" ? 2 : 3]) step = "p1";
    }
    if (
      ["p2", "p3", "p4", "p5"].includes(step) &&
      !intentions.slice(0, 2).includes(answers.intencion)
    )
      step = "p1";
    if (["p3", "p4", "p5"].includes(step) && !answers.capital) step = "p2";
    if (["p4", "p5"].includes(step) && !answers.zona) step = "p3";
    if (step === "p5" && Object.values(contactErrors(answers)).some(Boolean))
      step = "p4";
    const dates = businessDays();
    let appointment = { ...emptyAppointment };
    if (value.appointment && dates.includes(value.appointment.fecha)) {
      appointment.fecha = value.appointment.fecha;
      if (validAppointment(value.appointment, dates))
        appointment.hora = value.appointment.hora;
    }
    return { version: 1, step, answers, appointment };
  } catch {
    return null;
  }
}
export interface FranchiseLead {
  version: 1;
  requestId: string;
  formulario: string;
  intencion: string;
  capital: string;
  zona: string;
  nombre: string;
  telefono: string;
  email: string;
  consentimiento: {
    accepted: true;
    version: "frq-contact-v1";
    timestamp: string;
  };
  prioridad: "alta" | "normal";
  cita: Appointment & { timeZone: string };
  utm: Record<string, string>;
  pagina: string;
  timestamp: string;
}
export function attribution(url: string) {
  const params = new URL(url).searchParams;
  const result: Record<string, string> = {};
  [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_id",
    "fbclid",
    "gclid",
  ].forEach((k) => {
    const v = params.get(k);
    if (v) result[k] = v.slice(0, 500);
  });
  return result;
}
export function buildLead(
  answers: Answers,
  appointment: Appointment,
  url: string,
  requestId: string,
): FranchiseLead {
  if (
    Object.values(contactErrors(answers)).some(Boolean) ||
    !intentions.slice(0, 2).includes(answers.intencion) ||
    !capitals.includes(answers.capital) ||
    !zones.includes(answers.zona) ||
    !validAppointment(appointment)
  )
    throw new Error("Revisa tus respuestas antes de continuar.");
  const timestamp = new Date().toISOString();
  const page = new URL(url);
  page.search = "";
  page.hash = "";
  return {
    version: 1,
    requestId,
    formulario: "FRQ2026_TuDiriges_Intencion_v2",
    intencion: answers.intencion,
    capital: answers.capital,
    zona: answers.zona,
    nombre: normalizeName(answers.nombre),
    telefono: answers.telefono.replace(/\D/g, ""),
    email: answers.email.trim().toLowerCase(),
    consentimiento: { accepted: true, version: "frq-contact-v1", timestamp },
    prioridad: capitals.indexOf(answers.capital) >= 2 ? "alta" : "normal",
    cita: { ...appointment, timeZone: TIME_ZONE },
    utm: attribution(url),
    pagina: page.toString(),
    timestamp,
  };
}
export interface LeadTransport {
  submit(
    lead: FranchiseLead,
  ): Promise<{ status: "demo-complete" | "accepted"; requestId: string }>;
}
// No network or persistent contact storage: production transport belongs on our server in phase two.
export const demoTransport: LeadTransport = {
  async submit(lead) {
    return { status: "demo-complete", requestId: lead.requestId };
  },
};
export interface AgendaAdapter {
  days(): string[];
  available(date: string, hour: string): boolean;
}
export const demoAgenda: AgendaAdapter = {
  days: businessDays,
  available: (date, hour) =>
    hours.includes(hour) && !isBusy(date, hours.indexOf(hour)),
};
